// Components/MiniCRM/CreateDeal.jsx
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import Handler from "../../../Helpers/Handler";
import MainLayout from "../../../Partials/MainLayout";
import FeedLayout from "../../../Partials/FeedLayout";
import "../../../Assets/css/style.css";

// const BASE = "http://localhost/business_community/";
const BASE = "https://qnexus.in/community/";
/* ─── Debounce hook: waits 350ms after user stops typing ──── */
function useDebounce(value, delay = 350) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const CreateDeal = () => {
  const navigate = useNavigate();
  const location = useLocation();

  /* ── Pre-fill if navigated from a vendor profile ─────────── */
  const prefillId = location.state?.receiver_id || "";
  const prefillName = location.state?.receiver_name || "";

  /* ── Core form state ──────────────────────────────────────── */
  const [form, setForm] = useState({
    receiver_id: prefillId,  // actual user_id stored here
    title: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);

  /* ── Receiver search state ────────────────────────────────── */
  const [searchText, setSearchText] = useState(prefillName);
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(
    prefillId ? { user_id: prefillId, business_name: prefillName } : null
  );

  const debouncedSearch = useDebounce(searchText, 350);
  const wrapperRef = useRef(null);

  /* ── Close dropdown on outside click ─────────────────────── */
  useEffect(() => {
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* ── Hit users/search_list.php on debounced input change ─── */
  useEffect(() => {
    if (selectedUser && debouncedSearch === selectedUser.business_name) return;

    if (!debouncedSearch.trim()) {
      setSuggestions([]);
      setDropdownOpen(false);
      return;
    }

    let cancelled = false;

    (async () => {
      setSearching(true);
      try {
        const res = await Handler({
          method: "GET",
          url: `/users/search_list.php?search=${encodeURIComponent(debouncedSearch)}&limit=8`,
        });
        console.log("search_list response:", res);
        if (!cancelled && res?.success) {
          setSuggestions(res.data?.list || []);
          setDropdownOpen(true);
        }
      } catch {
        // silent — don't block the form for search failures
      } finally {
        if (!cancelled) setSearching(false);
      }
    })();

    return () => { cancelled = true; };
  }, [debouncedSearch]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── User picks a suggestion ──────────────────────────────── */
  const handleSelect = useCallback((user) => {
    console.log("Selected user:", JSON.stringify(user)); // 👈 stringify forces full print
    setSelectedUser(user);
    setSearchText(user.business_name);
    setForm((f) => ({ ...f, receiver_id: user.user_id }));
    setSuggestions([]);
    setDropdownOpen(false);
  }, []);

  /* ── Clear selection ──────────────────────────────────────── */
  const handleClear = () => {
    setSelectedUser(null);
    setSearchText("");
    setForm((f) => ({ ...f, receiver_id: "" }));
    setSuggestions([]);
  };

  /* ── Typing again after selecting invalidates the choice ──── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (selectedUser && val !== selectedUser.business_name) {
      setSelectedUser(null);
      setForm((f) => ({ ...f, receiver_id: "" }));
    }
  };

  /* ── Other form fields ────────────────────────────────────── */
  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  };

  /* ── Submit ───────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.receiver_id) {
      toast.error("Please search and select a receiver from the dropdown.");
      return;
    }
    setLoading(true);
    try {
      const res = await Handler({
        method: "POST",
        url: "/mini_crm/create.php",
        data: {
          receiver_id: parseInt(form.receiver_id),
          title: form.title.trim(),
          description: form.description.trim(),
        },
      });
      if (res.success) {
        toast.success("Deal created successfully!");
        navigate("/user/crm");
      } else {
        toast.error(res.message || "Failed to create deal");
      }
    } catch {
      toast.error("Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  /* ── Render ───────────────────────────────────────────────── */
  return (
    <MainLayout>
      <FeedLayout>
        <div className="crm-page">

          <div className="crm-header">
            <div>
              <h1 className="crm-title">New Deal</h1>
              <p className="crm-subtitle">Send a deal request to another vendor</p>
            </div>
            <button
              className="crm-btn crm-btn--outline"
              onClick={() => navigate("/user/crm")}
            >
              <i className="bx bx-arrow-back" /> Back
            </button>
          </div>

          <div className="crm-form-card">
            <form onSubmit={handleSubmit}>

              {/* ── Receiver search field ──────────────────── */}
              <div className="crm-field" ref={wrapperRef}>
                <label className="crm-label">
                  Receiver <span className="crm-required">*</span>
                </label>

                <div className="crm-receiver-wrap">
                  {selectedUser?.logo && (
                    // <img src={`http://localhost/business_community/${selectedUser.logo}`} alt="" className="crm-receiver-avatar" />
                    <img src={`https://qnexus.in/community/${selectedUser.logo}`} alt="" className="crm-receiver-avatar" />
                  )}

                  <input
                    className={`crm-input crm-receiver-input ${selectedUser ? "crm-input--selected" : ""}`}
                    type="text"
                    placeholder="Search by business name, city or phone…"
                    value={searchText}
                    onChange={handleSearchChange}
                    onFocus={() => suggestions.length && setDropdownOpen(true)}
                    autoComplete="off"
                  />

                  <span className="crm-receiver-suffix">
                    {searching ? (
                      <span className="crm-btn-spinner crm-spinner--sm" />
                    ) : searchText ? (
                      <button type="button" className="crm-clear-btn" onClick={handleClear} title="Clear">
                        <i className="bx bx-x" />
                      </button>
                    ) : (
                      <i className="bx bx-search" style={{ color: "var(--crm-muted, #94a3b8)" }} />
                    )}
                  </span>

                  {/* Suggestions dropdown */}
                  {dropdownOpen && suggestions.length > 0 && (
                    <ul className="crm-suggestions">
                      {suggestions.map((u) => (
                        <li
                          key={u.id}
                          className="crm-suggestion-item"
                          onMouseDown={() => handleSelect(u)}
                        >
                          {u.logo ? (
                            <img src={BASE + u.logo} alt="" className="crm-sug-avatar" />
                          ) : (
                            <span className="crm-sug-avatar crm-sug-avatar--fallback">
                              {u.business_name?.[0]?.toUpperCase() || "?"}
                            </span>
                          )}
                          <span className="crm-sug-info">
                            <span className="crm-sug-name">{u.business_name}</span>
                            {(u.city || u.area) && (
                              <span className="crm-sug-meta">
                                <i className="bx bx-map-pin" />
                                {[u.area, u.city].filter(Boolean).join(", ")}
                              </span>
                            )}
                          </span>
                          <span className="crm-sug-id">#{u.user_id}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  {/* No results */}
                  {dropdownOpen && !searching && suggestions.length === 0 && debouncedSearch.trim() && (
                    <ul className="crm-suggestions">
                      <li className="crm-suggestion-empty">
                        <i className="bx bx-search-alt" />
                        No vendors found for "{debouncedSearch}"
                      </li>
                    </ul>
                  )}
                </div>

                <input type="hidden" name="receiver_id" value={form.receiver_id} />

                <p className="crm-hint">
                  {selectedUser ? (
                    <>
                      <i className="bx bx-check-circle" style={{ color: "var(--crm-success, #22c55e)", marginRight: 4 }} />
                      Selected: <strong>{selectedUser.business_name}</strong>
                    </>
                  ) : (
                    "Type to search and select the vendor you want to deal with."
                  )}
                </p>
              </div>

              {/* ── Deal Title ────────────────────────────── */}
              <div className="crm-field">
                <label className="crm-label">Deal Title</label>
                <input
                  className="crm-input"
                  type="text"
                  name="title"
                  placeholder="e.g. Supply Agreement Q2 2026"
                  value={form.title}
                  onChange={handleChange}
                  maxLength={120}
                />
              </div>

              {/* ── Description ───────────────────────────── */}
              <div className="crm-field">
                <label className="crm-label">Description</label>
                <textarea
                  className="crm-input crm-textarea"
                  name="description"
                  placeholder="Describe the deal terms, scope, expectations…"
                  value={form.description}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                />
                <p className="crm-hint">{form.description.length}/500</p>
              </div>

              {/* ── Actions ───────────────────────────────── */}
              <div className="crm-form-actions">
                <button type="button" className="crm-btn crm-btn--outline" onClick={() => navigate("/user/crm")}>
                  Cancel
                </button>
                <button type="submit" className="crm-btn crm-btn--primary" disabled={loading}>
                  {loading ? (
                    <><span className="crm-btn-spinner" /> Sending…</>
                  ) : (
                    <><i className="bx bx-send" /> Send Deal Request</>
                  )}
                </button>
              </div>

            </form>
          </div>

        </div>
      </FeedLayout>
    </MainLayout>
  );
};

export default CreateDeal;
